import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Package, CheckSquare, Square } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BrandSmartLookupPurchase } from '../../services/brandsmart.api';

interface Props {
  purchases: BrandSmartLookupPurchase[];
  onBack: () => void;
  onNext: (selectedIds: string[]) => void;
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatPrice(price: number | null) {
  if (price == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price);
}

export default function LocateStep3Assets({ purchases, onBack, onNext }: Props) {
  const { t } = useTranslation('brandsmart');
  const [selectedIds, setSelectedIds] = useState<string[]>(purchases.map((p) => p.id));

  function toggle(id: string) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function toggleAll() {
    if (selectedIds.length === purchases.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(purchases.map((p) => p.id));
    }
  }

  if (purchases.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4 hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> {t('common.back')}
          </button>
          <h2 className="text-xl font-semibold mb-1">{t('locateStep3.noPurchasesTitle')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('locateStep3.noPurchasesBody')}
          </p>
        </div>
        <button
          onClick={onBack}
          className="w-full border rounded-lg px-4 py-3 text-sm font-medium hover:bg-muted transition-colors"
        >
          {t('locateStep3.tryDifferent')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t('common.back')}
        </button>
        <h2 className="text-xl font-semibold mb-1">{t('locateStep3.foundHeading', { count: purchases.length })}</h2>
        <p className="text-sm text-muted-foreground">
          {t('locateStep3.subtitle')}
        </p>
      </div>

      {/* Select all */}
      <button
        onClick={toggleAll}
        className="flex items-center gap-2 text-sm font-medium text-primary hover:underline"
      >
        {selectedIds.length === purchases.length ? (
          <><CheckSquare className="h-4 w-4" /> {t('common.deselectAll')}</>
        ) : (
          <><Square className="h-4 w-4" /> {t('common.selectAll')}</>
        )}
      </button>

      {/* Purchase cards */}
      <div className="space-y-3">
        {purchases.map((p) => {
          const isSelected = selectedIds.includes(p.id);
          return (
            <button
              key={p.id}
              onClick={() => toggle(p.id)}
              className={[
                'w-full text-left border-2 rounded-xl p-4 transition-colors flex gap-3',
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:border-muted-foreground/40',
              ].join(' ')}
            >
              {/* Thumbnail */}
              <div className="flex-shrink-0 h-14 w-14 rounded-lg bg-muted overflow-hidden flex items-center justify-center">
                {p.productImageUrl ? (
                  <img
                    src={p.productImageUrl}
                    alt={p.productName}
                    className="h-full w-full object-contain"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <Package className="h-6 w-6 text-muted-foreground" />
                )}
              </div>

              {/* Details */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-tight truncate">{p.productName}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{p.brand}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5">
                  <span className="text-xs text-muted-foreground">
                    {t('locateStep3.purchasedOn', { date: formatDate(p.purchaseDate) })}
                  </span>
                  <span className="text-xs text-muted-foreground">{formatPrice(p.purchasePrice ?? null)}</span>
                  <span className="text-xs text-muted-foreground truncate">
                    {p.deliveryCity}, {p.deliveryState}
                  </span>
                </div>
              </div>

              {/* Checkbox indicator */}
              <div className={`flex-shrink-0 h-5 w-5 rounded border-2 flex items-center justify-center transition-colors
                ${isSelected ? 'bg-primary border-primary' : 'border-border'}`}
              >
                {isSelected && (
                  <svg className="h-3 w-3 text-primary-foreground" fill="none" viewBox="0 0 12 12">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <button
        onClick={() => onNext(selectedIds)}
        disabled={selectedIds.length === 0}
        className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-3 text-sm font-medium disabled:opacity-50 transition-opacity"
      >
        {t('locateStep3.claimSelected', { count: selectedIds.length })}
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}
